import { useRef, useState } from 'react';
import { Copy, Share2 } from 'lucide-react';

import type { Vacancy } from '@/api/schema';
import { useToast } from '@/components/feedback/Toast';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { cx } from '@/lib/cx';
import { VacancyIconButton } from './VacancyIconButton';
import styles from './JobLinkActions.module.css';

export function JobLinkActions({
  job,
  iconOnly = false,
}: {
  job: Pick<Vacancy, 'id' | 'title'>;
  iconOnly?: boolean;
}) {
  const toast = useToast();
  const [action, setAction] = useState<'copy' | 'share' | null>(null);
  const [manualCopy, setManualCopy] = useState(false);
  const busy = useRef(false);
  const url = new URL(`/jobs/${encodeURIComponent(job.id)}`, window.location.origin).href;

  const copy = async () => {
    try {
      const clipboard = (navigator as Partial<Navigator>).clipboard;
      if (!clipboard) throw new Error('Clipboard unavailable');
      await clipboard.writeText(url);
      toast.success('Link copied.');
    } catch {
      setManualCopy(true);
    }
  };

  const run = async (next: 'copy' | 'share') => {
    if (busy.current) return;
    busy.current = true;
    setAction(next);
    setManualCopy(false);
    try {
      if (next === 'share' && typeof navigator.share === 'function') {
        try {
          await navigator.share({ title: `${job.title} · Flyst Careers`, url });
          return;
        } catch (error) {
          // Cancelling the device's share menu should leave the clipboard alone.
          if (error instanceof DOMException && error.name === 'AbortError') return;
        }
      }
      await copy();
    } finally {
      busy.current = false;
      setAction(null);
    }
  };

  return (
    <div
      className={cx(styles.root, iconOnly && manualCopy && styles.expanded)}
      role="group"
      aria-label={`Share ${job.title}`}
    >
      <div className={styles.buttons}>
        {(
          [
            { key: 'share', label: 'Share', Icon: Share2 },
            { key: 'copy', label: 'Copy link', Icon: Copy },
          ] as const
        ).map(({ key, label, Icon }) => {
          const props = {
            icon: <Icon size={iconOnly ? 18 : 16} aria-hidden="true" />,
            loading: action === key,
            disabled: action !== null,
            onClick: () => void run(key),
          };
          return iconOnly ? (
            <VacancyIconButton key={key} label={label} {...props} />
          ) : (
            <Button key={key} variant="ghost" size="sm" {...props}>
              {label}
            </Button>
          );
        })}
      </div>
      {manualCopy ? (
        <>
          <span className={styles.feedback} role="status">
            Select and copy the job link below.
          </span>
          <Field label="Job link">
            {(ids) => (
              <Input
                {...ids}
                readOnly
                value={url}
                onFocus={(event) => {
                  event.currentTarget.select();
                }}
                onClick={(event) => {
                  event.currentTarget.select();
                }}
              />
            )}
          </Field>
        </>
      ) : null}
    </div>
  );
}
